import { GroupedAttributesWithKey } from './models';
import { SelectAttributesManager } from './SelectAttributesManager';
import { DataNode } from '../common';
import { FaTimesCircle } from 'react-icons/fa/index.js';
import { Tooltip, HStack, Text, Icon } from '@chakra-ui/react';
import {
  MaxAllowedAttributes,
  MaxAllowedScoreDistributioReportsAttributes,
} from '@ssar-app/domain';
import { MouseEventHandler } from 'react';

type HasCount = {
  count: number;
};

type CountCollector = Record<
  string,
  HasCount & {
    categories: Record<string, HasCount>;
  }
>;

interface RemoveIconProps {
  onClick: MouseEventHandler<SVGSVGElement> | undefined;
}

const RemoveIcon = (props: RemoveIconProps) => {
  return <Icon onClick={props.onClick} as={FaTimesCircle} fontSize='lg' />;
};

/* To convert GroupedAttributes to tree format */
export const getTreeSelectView = (
  attributesManager: SelectAttributesManager,
  flag: boolean,
): DataNode[] => {
  let countCollector: CountCollector = {};
  countCollector = attributesManager
    .getCheckedUnpackedKeys()
    .reduce((acc, curr) => {
      if (!acc[curr.group]) {
        acc[curr.group] = {
          count: 0,
          categories: {},
        };
      }
      if (!acc[curr.group].categories[curr.category]) {
        acc[curr.group].categories[curr.category] = {
          count: 0,
        };
      }
      acc[curr.group].count += 1;

      acc[curr.group].categories[curr.category].count += 1;
      return acc;
    }, {} as CountCollector);
  /* console.log(
    'some: ',
    attributesManager.getGroupedAttributes(true)[0],
  ); */

  const maxSelectableAtribtuesCount = flag
    ? MaxAllowedAttributes - attributesManager.getCheckedKeysSet().size
    : MaxAllowedScoreDistributioReportsAttributes -
      attributesManager.getCheckedKeysSet().size;
  const checkedCodes = attributesManager.getCheckedCodes();
  const checkedKeys = attributesManager.getCheckedKeysSet();
  return attributesManager
    .getGroupedAttributes(true)
    .map((group: GroupedAttributesWithKey) => {
      const groupSelectedAttributesCount =
        countCollector[group.name] === undefined
          ? 0
          : countCollector[group.name].count;
      const groupTotalAttributesCount = group.categories.reduce(
        (accumulator, currentValue) =>
          accumulator + currentValue.attributes.length,
        0,
      );
      const groupAvailableAttributesCount =
        groupTotalAttributesCount - groupSelectedAttributesCount;
      const canSelectGroupNode =
        maxSelectableAtribtuesCount >= groupAvailableAttributesCount;
      return {
        title:
          group.name +
          ' (' +
          groupSelectedAttributesCount +
          ' of ' +
          groupTotalAttributesCount +
          ')',
        checkable: canSelectGroupNode,
        key: group.key,
        children: group.categories
          .map((category) => {
            const collectedCategory =
              countCollector[group.name]?.categories?.[category.name];
            const categorySelectedAttributesCount: number =
              collectedCategory?.count || 0;
            const categoryTotalAttributesCount = category.attributes.length;
            const categoryAvailableAttributeCount =
              categoryTotalAttributesCount - categorySelectedAttributesCount;
            const canSelectCategoryNode =
              maxSelectableAtribtuesCount >= categoryAvailableAttributeCount;

            return {
              title:
                category.name +
                ' (' +
                categorySelectedAttributesCount +
                ' of ' +
                categoryTotalAttributesCount +
                ')',
              checkable: canSelectCategoryNode,
              key: category.key,
              children: category.attributes
                .filter((attribute) => !attribute.isHiddenFilter)
                .map((attribute) => {
                  const isChecked = checkedKeys.has(attribute.key);
                  const isDuplicate =
                    !isChecked && checkedCodes.includes(attribute.code);
                  return {
                    title: (
                      <HStack>
                        <Tooltip
                          label={attribute.description}
                          placement='right'
                        >
                          {attribute.code}
                        </Tooltip>
                      </HStack>
                    ),
                    checkable: !isChecked
                      ? !isDuplicate
                        ? maxSelectableAtribtuesCount >= 1
                        : false
                      : true,
                    key: attribute.key,
                    leafCode: attribute.code,
                  };
                }),
            };
          })
          .filter((a) => a.children.length > 0),
      };
    })
    .filter((a) => a.children.length > 0);
};

export const getTreeSelectedView = (
  attributesManager: SelectAttributesManager,
  onRemoveItem: (itemKey: string, forSelectedListTree: boolean) => void,
  selectWhichToBeRemoved: boolean,
  selectedAttributeManager: SelectAttributesManager,
  selectWhichAttributeManager?: boolean,
): DataNode[] => {
  /* console.log(
    'some: ',
    attributesManager.getGroupedAttributes(false)[0],
  ); */

  const attrbManager = selectWhichAttributeManager
    ? selectedAttributeManager
    : attributesManager;
  let countCollector: CountCollector = {};
  countCollector = attrbManager.getCheckedUnpackedKeys().reduce((acc, curr) => {
    if (!acc[curr.group]) {
      acc[curr.group] = {
        count: 0,
        categories: {},
      };
    }
    if (!acc[curr.group].categories[curr.category]) {
      acc[curr.group].categories[curr.category] = {
        count: 0,
      };
    }
    acc[curr.group].count += 1;
    acc[curr.group].categories[curr.category].count += 1;
    return acc;
  }, {} as CountCollector);

  const maxSelectableAtribtuesCount =
    MaxAllowedScoreDistributioReportsAttributes -
    (selectWhichAttributeManager
      ? selectedAttributeManager.getCheckedKeysSet().size
      : attributesManager.getCheckedKeysSet().size);
  const checkedCodes = selectWhichAttributeManager
    ? selectedAttributeManager.getCheckedCodes()
    : attributesManager.getCheckedCodes();
  const checkedKeys = selectWhichAttributeManager
    ? selectedAttributeManager.getCheckedKeysSet()
    : attributesManager.getCheckedKeysSet();

  return attributesManager
    .getGroupedAttributes(false)
    .map((group: GroupedAttributesWithKey) => {
      const groupSelectedAttributesCount =
        countCollector[group.name] === undefined
          ? 0
          : countCollector[group.name].count;
      const groupTotalAttributesCount = group.categories.reduce(
        (accumulator, currentValue) =>
          accumulator + currentValue.attributes.length,
        0,
      );
      const groupAvailableAttributesCount =
        groupTotalAttributesCount - groupSelectedAttributesCount;
      const canSelectGroupNode =
        maxSelectableAtribtuesCount >= groupAvailableAttributesCount;

      return {
        title: (
          <HStack>
            <Text>
              {group.name +
                ' (' +
                group.categories.reduce(
                  (accumulator, currentValue) =>
                    accumulator + currentValue.attributes.length,
                  0,
                ) +
                ')'}
            </Text>
            <Tooltip label='Remove' placement='right'>
              <RemoveIcon
                onClick={() => onRemoveItem(group.key, selectWhichToBeRemoved)}
              />
            </Tooltip>
          </HStack>
        ),
        key: group.key,
        checkable: canSelectGroupNode,
        children: group.categories
          .map((category) => {
            const collectedCategory =
              countCollector[group.name]?.categories?.[category.name];
            const categorySelectedAttributesCount: number =
              collectedCategory?.count || 0;
            const categoryTotalAttributesCount = category.attributes.length;
            const categoryAvailableAttributeCount =
              categoryTotalAttributesCount - categorySelectedAttributesCount;
            const canSelectCategoryNode =
              maxSelectableAtribtuesCount >= categoryAvailableAttributeCount;
            return {
              title: (
                <HStack>
                  <Text>
                    {category.name + ' (' + category.attributes.length + ')'}
                  </Text>
                  <Tooltip label='Remove' placement='right'>
                    <RemoveIcon
                      onClick={() =>
                        onRemoveItem(category.key, selectWhichToBeRemoved)
                      }
                    />
                  </Tooltip>
                </HStack>
              ),
              key: category.key,
              checkable: canSelectCategoryNode,
              children: category.attributes
                .filter((attribute) => !attribute.isHiddenFilter)
                .map((attribute) => {
                  const isChecked = checkedKeys.has(attribute.key);
                  const isDuplicate =
                    !isChecked && checkedCodes.includes(attribute.code);
                  return {
                    title: (
                      <HStack>
                        <Tooltip
                          label={attribute.description}
                          placement='right'
                          hasArrow
                          ml={8}
                        >
                          {attribute.code}
                        </Tooltip>
                        <Tooltip label='Remove' placement='right'>
                          <RemoveIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveItem(
                                attribute.key,
                                selectWhichToBeRemoved,
                              );
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    ),
                    checkable: !isChecked
                      ? !isDuplicate
                        ? maxSelectableAtribtuesCount >= 1
                        : false
                      : true,
                    key: attribute.key,
                    leafCode: attribute.code,
                  };
                }),
            };
          })
          .filter((a) => a.children.length > 0),
      };
    })
    .filter((a) => a.children.length > 0);
};
